import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const BUSINESS_TYPES = [
  "General Business", "Retail / E-commerce", "Restaurant / Food",
  "Construction", "Healthcare", "Trucking / Transport",
  "Professional Services", "Manufacturing",
];
const TIB = ["Less than 6 months", "6-12 months", "1-2 years", "2-5 years", "5+ years"];

export default function ApplicationDialog({ open, onClose, prefill = {} }) {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    business_name: "",
    business_type: "General Business",
    time_in_business: "1-2 years",
    monthly_revenue: 20000,
    credit_score: 650,
    funding_amount: 50000,
    funding_purpose: "",
    notes: "",
    estimated_range_low: 0,
    estimated_range_high: 0,
  });

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, ...prefill }));
      setStep(0);
      setSubmitted(false);
    }
  }, [open, prefill]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => setStep((s) => Math.min(s + 1, 2));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setSubmitted(true);
      toast.success("Application received!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="apply-dialog"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[95vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            data-testid="apply-close-btn"
            onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 grid place-items-center z-10"
          >
            <X className="w-4 h-4 text-slate-600" />
          </button>

          {submitted ? (
            <div className="p-10 md:p-14 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 grid place-items-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-display text-3xl font-black text-slate-900">
                Application received!
              </h3>
              <p className="mt-3 text-slate-500 max-w-md mx-auto">
                A Rocket Cap funding specialist will reach out within one
                business hour with your matched options.
              </p>
              <button
                onClick={onClose}
                data-testid="apply-done-btn"
                className="mt-8 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold px-7 py-3 text-sm uppercase tracking-widest"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-br from-[#0b1121] to-[#312e81] text-white p-8">
                <p className="text-xs uppercase tracking-[0.25em] text-purple-300 font-bold">
                  Step {step + 1} of 3
                </p>
                <h3 className="font-display text-3xl font-black mt-2">
                  {step === 0 && "Tell us about you"}
                  {step === 1 && "Your business"}
                  {step === 2 && "Funding needs"}
                </h3>
                <div className="mt-5 flex gap-2">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={`h-1.5 flex-1 rounded-full transition ${
                        i <= step ? "bg-purple-400" : "bg-white/15"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="p-8 space-y-4">
                {step === 0 && (
                  <>
                    <Field label="Full name">
                      <input
                        data-testid="apply-fullname"
                        required
                        value={form.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        className={fieldCls}
                      />
                    </Field>
                    <Field label="Email">
                      <input
                        data-testid="apply-email"
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className={fieldCls}
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        data-testid="apply-phone"
                        required
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        className={fieldCls}
                      />
                    </Field>
                  </>
                )}

                {step === 1 && (
                  <>
                    <Field label="Business name">
                      <input
                        data-testid="apply-business-name"
                        value={form.business_name}
                        onChange={(e) => update("business_name", e.target.value)}
                        className={fieldCls}
                      />
                    </Field>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field label="Business type">
                        <select
                          data-testid="apply-business-type"
                          value={form.business_type}
                          onChange={(e) => update("business_type", e.target.value)}
                          className={fieldCls}
                        >
                          {BUSINESS_TYPES.map((b) => (
                            <option key={b}>{b}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Time in business">
                        <select
                          data-testid="apply-time-in-business"
                          value={form.time_in_business}
                          onChange={(e) => update("time_in_business", e.target.value)}
                          className={fieldCls}
                        >
                          {TIB.map((t) => (
                            <option key={t}>{t}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field label="Monthly revenue ($)">
                        <input
                          data-testid="apply-monthly-revenue"
                          type="number"
                          value={form.monthly_revenue}
                          onChange={(e) => update("monthly_revenue", parseInt(e.target.value) || 0)}
                          className={fieldCls}
                        />
                      </Field>
                      <Field label="Credit score">
                        <input
                          data-testid="apply-credit-score"
                          type="number"
                          value={form.credit_score}
                          onChange={(e) => update("credit_score", parseInt(e.target.value) || 0)}
                          className={fieldCls}
                        />
                      </Field>
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <Field label="How much funding do you need? ($)">
                      <input
                        data-testid="apply-funding-amount"
                        type="number"
                        value={form.funding_amount}
                        onChange={(e) => update("funding_amount", parseInt(e.target.value) || 0)}
                        className={fieldCls}
                      />
                    </Field>
                    <Field label="What's the funding for?">
                      <input
                        data-testid="apply-funding-purpose"
                        placeholder="e.g. Equipment, working capital, expansion"
                        value={form.funding_purpose}
                        onChange={(e) => update("funding_purpose", e.target.value)}
                        className={fieldCls}
                      />
                    </Field>
                    <Field label="Anything else we should know?">
                      <textarea
                        data-testid="apply-notes"
                        rows={3}
                        value={form.notes}
                        onChange={(e) => update("notes", e.target.value)}
                        className={fieldCls + " resize-none"}
                      />
                    </Field>
                  </>
                )}

                <div className="flex items-center justify-between pt-4">
                  {step > 0 ? (
                    <button
                      data-testid="apply-back-btn"
                      onClick={back}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                  ) : (
                    <span />
                  )}

                  {step < 2 ? (
                    <button
                      data-testid="apply-next-btn"
                      onClick={next}
                      disabled={
                        (step === 0 && (!form.full_name || !form.email || !form.phone))
                      }
                      className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white font-bold px-7 py-3 text-sm uppercase tracking-widest shadow"
                    >
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      data-testid="apply-submit-btn"
                      onClick={submit}
                      disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 disabled:opacity-50 text-white font-bold px-7 py-3 text-sm uppercase tracking-widest shadow-xl shadow-purple-500/30"
                    >
                      {submitting ? "Submitting…" : "Submit Application"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const fieldCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
